import GameObject from "./GameObject";
import { Input, KeyCode } from "./Input";
import Listener from "../util/Listener";
import MonoBehavior from "./MonoBehavior";
import { Time } from "./Time";
import { AvgArray, formatTime, isMobile, isOutsideBounds, MetersToUnits, remapToRange, toUnits, toUnitString } from "../util";
import Vector2 from "../math/Vector2";
import { Shapes } from "../util/Shapes";
import { Physics } from "../math/Physics";
import Player from "../scripts/Player";
import CTFFlag from "../scripts/CTFFlag";

export interface EngineOptions {
    background?: string;
    foreground?: string;
}

export default class Engine {
    protected _running:boolean = false;
    protected _boundingBox: DOMRect;
    isMobile = isMobile();

    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    camera: Vector2 = Vector2.zero;
    drawGizmos: boolean = true;
    viewport = {
        bgColor: '#000010',
        fColor: '#e0e0e0'
    };
    gameObjects: GameObject[] = [];
    listeners: Listener = new Listener();

    touchDownLeftPos: Vector2|undefined;
    touchDownRightPos: Vector2|undefined;
    touchLeftPos: Vector2|undefined;
    touchRightPos: Vector2|undefined;

    touchMoveIndex: number = 0;
    touchShootIndex: number = 1;

    get width() { return this.canvas.width; }
    get height() { return this.canvas.height; }

    get isRunning() {return this._running;}

    set width(n: number) { this.canvas.width = n; }
    set height(n: number) { this.canvas.height = n; }

    get center() {return new Vector2(this.width/2, this.height/2);}

    get cameraDrawPos() {
        return this.camera.sub(this.center);
    }

    get mouseWorldPosition() {
        return Input.mousePosition.add(this.cameraDrawPos);
    }

    constructor(canvas: HTMLCanvasElement, options?: EngineOptions) {

        if (options?.background) this.viewport.bgColor = options.background;
        if (options?.foreground) this.viewport.fColor = options.foreground;

        if (canvas == undefined) {
            canvas = document.createElement("canvas");
        }

        canvas.tabIndex = 0;
        this.canvas = canvas;
        this.canvas.className = 'viewport';
        this.canvas.tabIndex = 0;
        this.canvas.autofocus = true;
        this.ctx = this.canvas.getContext("2d") as CanvasRenderingContext2D;

        this._boundingBox = this.canvas.getBoundingClientRect();
    }

    toScreenPosition(position: Vector2) {
        return position.sub(this.cameraDrawPos);
    }

    toWorldPosition(position: Vector2) {
        return position.add(this.cameraDrawPos);
    }

    appendToElement(el: Element) {
        el.appendChild(this.canvas);
    }

    GetGameObject(id: string) {
        return this.gameObjects.filter(v => v.id === id)[0];
    }

    GetGameObjectsBehaviors<T=any>(behavior: typeof MonoBehavior): T[] {
        return this.gameObjects.map(v => v.GetScript(behavior)).filter(v => v);
    }

    NewGameObject(id?: string) {
        const obj: GameObject = new GameObject(this);
        if (id != undefined) obj.id = id;
        this.gameObjects.push(obj);
        return obj;
    }

    

    NewGameObjectWithScript<T = any>(type: typeof MonoBehavior, options: {
        id?: string;
        position?: Vector2;
        initialValues?: Record<string, any>;
    } = {}): [GameObject, T] {
        
        const obj: GameObject = new GameObject(this);

        if (options.position) {
            obj.transform.position = options.position.clone();
        }

        if (options.id != undefined) obj.id = options.id;

        const script: any = obj.AddScript(type);

        if (options.initialValues) {
            Object.assign(script, options.initialValues);
        }

        if (this._running) script.Start();

        this.gameObjects.push(obj);

        return [obj, script];
    }

    AddGameObject(obj: GameObject): GameObject|undefined {
        for (let i = 0; i < this.gameObjects.length; i++) {
            const gameObj: GameObject = this.gameObjects[i];
            if (obj.id === gameObj.id) return;
        }

        this.gameObjects.push(obj);

        return obj;
    }

    RemoveGameObject(id: string): void;
    RemoveGameObject(obj: GameObject): void;
    RemoveGameObject(idOrObj: string|GameObject) {
        const id = typeof idOrObj === "string" ? idOrObj : idOrObj.id;
        const index = this.gameObjects.map(v => v.id).indexOf(id);

        if (index < 0) return

        this.gameObjects.splice(index,1);
    }

    Resize() {
        // this.width = 1280;
        // this.height = 720;

        // const scale = Math.min(window.innerWidth / this.width, window.innerHeight / this.height);
        // const winSize = {x: this.width * scale, y: this.height * scale};

        // this.canvas.style.width = Math.floor(winSize.x) + 'px';
        // this.canvas.style.height = Math.floor(winSize.y) + 'px';



        this.width = window.innerWidth;
        this.height = window.innerHeight;

        this._boundingBox = this.canvas.getBoundingClientRect();
    }

    protected Update() {
        this.gameObjects.forEach(obj => {
            obj.scripts.forEach(script => {
                script.Update();
            })
        })
    }

    protected LateUpdate() {
        this.gameObjects.forEach(obj => {
            obj.scripts.forEach(script => {
                script.LateUpdate();
            })
        })
    }

    // drawcount = 0;
    protected onBeforeDraw() {}
    protected onAfterDraw() {}
    protected Draw() {
        this.ctx.fillStyle = this.viewport.bgColor;
        this.ctx.fillRect(0, 0, this.width, this.height);

        this.onBeforeDraw();
        // this.drawcount = 0;

        const sortedGameObjectsByLayer = [...this.gameObjects];
        sortedGameObjectsByLayer.sort((a,b) => a.layer - b.layer);
        const cullDist = Math.max(this.width, this.height) * 1.5;
        sortedGameObjectsByLayer.forEach(obj => {
            obj.scripts.forEach(script => {
                // if (
                //     obj.id !== "game-manager" && !(script instanceof Player) &&
                //     !(script instanceof CTFFlag) &&
                //     Vector2.distance(this.camera, script.transform.position) >= cullDist
                // ) return;

                script.Draw();
                // this.drawcount++;
            })
        })

        // console.log(`Drew ${this.drawcount} behaviors this frame`);
        this.onAfterDraw();
    }

    protected DrawGizmos() {
        if (!this.drawGizmos) return;

        this.gameObjects.forEach(obj => {
            obj.scripts.forEach(script => {
                script.DrawGizmos();
            })
        })

        const fps: number = Math.floor(1/Time.deltaTime);

        this.drawTexts(
            [
                `${fps} TPS`,
                `${Math.floor(AvgArray(this.realFPS))} FPS`,
                `${this.width}x${this.height}`,
                `${this.mouseWorldPosition.x}, ${this.mouseWorldPosition.y} (${Input.mousePosition.x}, ${Input.mousePosition.y})`,
                `${this.gameObjects.length.toLocaleString()} objects`
            ],
            Vector2.one.scale(8),
            {
                color: this.viewport.fColor
            }
        );
    }

    protected onBeforeUpdate() {}

    protected targetDelta = 1/60;
    protected accumulator = 0;
    protected maxSteps = 60;
    protected realFPS: number[] = [];
    protected realTime = 0;

    GetRealTime() {return this.realTime;}

    protected Tick = (ts: number) => {


        if (this._running) {
            window.requestAnimationFrame(this.Tick);
            // setTimeout(() => {
            //     this.Tick(performance.now());
            // }, 1000/120);
        }

        const newTime = ts/1000;

        let frameTime = (newTime - this.realTime);
        this.realFPS.push(1/frameTime);

        if (this.realFPS.length > 1024) {
            this.realFPS.shift();
        }

        if (frameTime > 0.1) frameTime = 0.1;

        this.realTime = newTime;


        this.accumulator += frameTime;

        Time.deltaTime = this.targetDelta * Time.scale;
        let steps = 0;
        while (this.accumulator >= this.targetDelta) {
            this.onBeforeUpdate();
            this.Update();
            this.LateUpdate();

            Input.ClearThisFrame();

            this.accumulator -= this.targetDelta;
            Time.time += Time.deltaTime;
            
            if (++steps >= this.maxSteps) break;
        }

        this.Draw();
        this.DrawGizmos();
    }

    protected onInit() {}
    protected Init() {

        this.Resize();

        window.addEventListener("resize", () => {
            this.Resize();
        });

        if (!this.isMobile) {
            this.canvas.addEventListener("mousedown", e => {
                e.preventDefault();
                Input.send("mousedown", e.button);
                this.canvas.focus();
            })
            this.canvas.addEventListener("mouseup", e => {
                e.preventDefault();
                Input.send("mouseup", e.button);
            })
            this.canvas.addEventListener("mouseout", e => {
                e.preventDefault();
                Input.send("mouseup", e.button);
            })
            this.canvas.addEventListener("mousemove", e => {
                Input.send("mousemove", new Vector2(
                    Math.floor(remapToRange(e.clientX, [this._boundingBox.left, this._boundingBox.left + this._boundingBox.width], [0, this.width])),
                    Math.floor(remapToRange(e.clientY, [this._boundingBox.top, this._boundingBox.top + this._boundingBox.height], [0, this.height]))
                ));
            });
            // this.canvas.addEventListener("mousemove", e => {
            //     const box = this.canvas.getBoundingClientRect();
            //     const scale = this.canvas.width / box.width;
            //     Input.send("mousemove", new Vector2(
            //         (e.clientX - box.left) * scale,
            //         (e.clientY - box.top) * scale
            //     ));
            // });
            this.canvas.addEventListener("contextmenu", e => {
                e.preventDefault();
                return false;
            });
    
            this.canvas.addEventListener("keydown", e => {
                e.preventDefault();
                Input.send("keydown", e.code);
            })
            this.canvas.addEventListener("keyup", e => {
                e.preventDefault();
                Input.send("keyup", e.code);
            })
        } else {
            this.TouchInit();
        }

        this.onInit();
    }

    protected TouchInit() {
        //Mobile/touch device init
        
    }

    Start() {
        if (this._running) return;

        this._running = true;

        this.Init();

        window.requestAnimationFrame(this.Tick);
        // this.Tick(performance.now());
    }

    //#region Draw Helper Functions
    FillRect(position: Vector2, size: Vector2) {

        this.ctx.fillRect(position.x - this.cameraDrawPos.x, position.y - this.cameraDrawPos.y, size.x, size.y);
    }
    FillRectScreen(position: Vector2, size: Vector2) {

        this.ctx.fillRect(position.x, position.y, size.x, size.y);
    }

    FillRectFromCenter(position: Vector2, size: Vector2) {

        this.ctx.fillRect(position.x - size.x/2 - this.cameraDrawPos.x, position.y - size.y/2 - this.cameraDrawPos.y, size.x, size.y);
    }

    FillRectScreenFromCenter(position: Vector2, size: Vector2) {

        this.ctx.fillRect(position.x - size.x/2, position.y - size.y/2, size.x, size.y);
    }
    
    
    StrokeRect(position: Vector2, size: Vector2) {

        this.ctx.strokeRect(position.x - this.cameraDrawPos.x, position.y - this.cameraDrawPos.y, size.x, size.y);
    }

    StrokeRectScreenFromCenter(position: Vector2, size: Vector2) {

        this.ctx.strokeRect(position.x - size.x/2, position.y - size.y/2, size.x, size.y);
    }

    GradientCircle(position: Vector2, radius: number, colors: {
        start: string;
        end: string;
    }) {
        const bak = this.ctx.fillStyle;
        const screenPos = this.toScreenPosition(position);
        const g = this.ctx.createRadialGradient(screenPos.x, screenPos.y, 0, screenPos.x, screenPos.y, radius);
        g.addColorStop(0, colors.start);
        g.addColorStop(1, colors.end);
        this.ctx.fillStyle = g;
        this.FillCircle(position, radius);
        this.ctx.fillStyle = bak;
    }

    FillCircle(position: Vector2, radius: number) {
        position = position.sub(this.cameraDrawPos);
        this.ctx.beginPath();
        this.ctx.arc(position.x, position.y, radius, 0, 2 * Math.PI);
        this.ctx.fill();
        this.ctx.closePath();
    }
    StrokeCircle(position: Vector2, radius: number) {
        position = position.sub(this.cameraDrawPos);
        this.ctx.beginPath();
        this.ctx.arc(position.x, position.y, radius, 0, 2 * Math.PI);
        this.ctx.stroke();
        this.ctx.closePath();
    }
    FillCircleScreen(position: Vector2, radius: number) {
        this.ctx.beginPath();
        this.ctx.arc(position.x, position.y, radius, 0, 2 * Math.PI);
        this.ctx.fill();
        this.ctx.closePath();
    }
    StrokeCircleScreen(position: Vector2, radius: number) {
        this.ctx.beginPath();
        this.ctx.arc(position.x, position.y, radius, 0, 2 * Math.PI);
        this.ctx.stroke();
        this.ctx.closePath();
    }

    Line(...points: Vector2[]) {
        if (points.length < 2) return;

        points = points.map(v => v.sub(this.cameraDrawPos));
        this.ctx.beginPath();
        this.ctx.moveTo(points[0].x, points[0].y);

        for (let i = 1; i < points.length; i++) {
            this.ctx.lineTo(points[i].x, points[i].y);
        }

        this.ctx.stroke();
        this.ctx.closePath();
    }

    LineScreen(...points: Vector2[]) {
        if (points.length < 2) return;

        this.ctx.beginPath();
        this.ctx.moveTo(points[0].x, points[0].y);

        for (let i = 1; i < points.length; i++) {
            this.ctx.lineTo(points[i].x, points[i].y);
        }

        this.ctx.stroke();
        this.ctx.closePath();
    }

    strokeShape(...points: Vector2[]) {
        if (points.length < 2) return;

        points = points.map(v => v.sub(this.cameraDrawPos));
        this.ctx.beginPath();
        this.ctx.moveTo(points[0].x, points[0].y);

        for (let i = 1; i < points.length; i++) {
            this.ctx.lineTo(points[i].x, points[i].y);
        }

        this.ctx.lineTo(points[0].x, points[0].y);

        this.ctx.stroke();
        this.ctx.closePath();
    }

    strokeShapeScreenPos(...points: Vector2[]) {
        if (points.length < 2) return;
        
        this.ctx.beginPath();
        this.ctx.moveTo(points[0].x, points[0].y);

        for (let i = 1; i < points.length; i++) {
            this.ctx.lineTo(points[i].x, points[i].y);
        }

        this.ctx.lineTo(points[0].x, points[0].y);

        this.ctx.stroke();
        this.ctx.closePath();
    }

    fillShape(...points: Vector2[]) {
        if (points.length < 2) return;

        points = points.map(v => v.sub(this.cameraDrawPos));
        this.ctx.beginPath();
        this.ctx.moveTo(points[0].x, points[0].y);

        for (let i = 1; i < points.length; i++) {
            this.ctx.lineTo(points[i].x, points[i].y);
        }

        this.ctx.lineTo(points[0].x, points[0].y);

        this.ctx.fill();
        this.ctx.closePath();
    }
    fillShapeScreenPos(...points: Vector2[]) {
        if (points.length < 2) return;

        
        this.ctx.beginPath();
        this.ctx.moveTo(points[0].x, points[0].y);

        for (let i = 1; i < points.length; i++) {
            this.ctx.lineTo(points[i].x, points[i].y);
        }

        this.ctx.lineTo(points[0].x, points[0].y);

        this.ctx.fill();
        this.ctx.closePath();
    }

    drawTexts(texts: string[], position: Vector2, options?: {
        align?: CanvasTextAlign;
        baseline?: CanvasTextBaseline;
        color?: string;
        size?: number;
    }) {
        const bak = this.ctx.fillStyle;
        position = position.clone();
        const fontBak = this.ctx.font;
        this.ctx.textAlign = options?.align || "left";
        this.ctx.textBaseline = options?.baseline || "top";
        
        if (options?.color != undefined) {
            this.ctx.fillStyle = options.color;
        }

        if (options?.baseline === "middle") {
            position.y -= (8*(texts.length-1));
        }

        if (options?.baseline === "bottom") {
            position.y -= (16*(texts.length-1));
        }

        if (options?.size) {
            this.ctx.font = `${options.size}px sans-serif`;
        }

        for (let i = 0; i < texts.length; i++) {

            let y = position.y + i * (options?.size || 10);

            this.ctx.fillText(texts[i], position.x, y);
        }

        
        this.ctx.font = fontBak;
        this.ctx.fillStyle = bak;
    }

    drawTextsWorldPosition(texts: string[], position: Vector2, options?: {
        align?: CanvasTextAlign;
        baseline?: CanvasTextBaseline;
        color?: string;
        size?: number;
    }) {
        position = position.sub(this.cameraDrawPos);
        const bak = this.ctx.fillStyle;
        const fontBak = this.ctx.font;
        this.ctx.textAlign = options?.align || "left";
        this.ctx.textBaseline = options?.baseline || "top";
        
        if (options?.color != undefined) {
            this.ctx.fillStyle = options.color;
        }

        if (options?.baseline === "middle") {
            position.y -= (8*(texts.length-1));
        }

        if (options?.baseline === "bottom") {
            position.y -= (16*(texts.length-1));
        }

        if (options?.size != undefined) {
            this.ctx.font = `${options.size}px sans-serif`;
        }

        for (let i = 0; i < texts.length; i++) {

            let y = position.y + i * 16;

            this.ctx.fillText(texts[i], position.x, y);
        }

        this.ctx.fillStyle = bak;
        this.ctx.font = fontBak;
    }

    drawTextWrap(text: string, position: Vector2, maxWidth: number, align: CanvasTextAlign="left", baseline:CanvasTextBaseline="top") {

        let input = text.split(" ");
        const output: string[] = [];
        let str = input[0];
        for (let i = 1; i < input.length; i++) {
            const nextStr = str + ` ${input[i]}`;

            if (this.ctx.measureText(nextStr).width <= maxWidth) {
                str = nextStr;
            } else {
                output.push(str);
                str = `${input[i]}`;
            }
        }

        if (str) output.push(str);

        this.drawTexts(output, position, {align, baseline});
    }

    drawUIMarker(worldPosition: Vector2, color: string, velocity: number) {
        const targetScreenPos = this.toScreenPosition(worldPosition);

        

        if (isOutsideBounds(targetScreenPos.x, 0, this.width) || isOutsideBounds(targetScreenPos.y, 0, this.height)) {
            const line: Shapes.Line = {
                start: this.center,
                end: targetScreenPos
            };

            const vectorToScreenCenter = this.center.sub(targetScreenPos);
            const dist = vectorToScreenCenter.magnitude;
            vectorToScreenCenter.normalize();

            const collision = Physics.LineRectCollision(line, {
                center: this.center,
                size: new Vector2(this.width, this.height)
            });

            let drawPt: Vector2;

            if (collision) {
                drawPt = collision.add(vectorToScreenCenter.scale(16));
            } else {
                drawPt = this.center.sub(vectorToScreenCenter.scale(256));
            }

            const p1 = drawPt.sub(vectorToScreenCenter.scale(8));
            const p2 = drawPt.sub(vectorToScreenCenter.rotate(Math.PI/4).scale(8));
            const p3 = drawPt.sub(vectorToScreenCenter.rotate(-Math.PI/4).scale(8));
            this.ctx.strokeStyle = color;
            this.ctx.fillStyle = color;
            this.ctx.lineWidth = 2;
            this.LineScreen(p2, p1, p3);
            this.drawTexts([
                toUnitString(MetersToUnits(toUnits(dist))),
                velocity === 0 ? '--:--:--' : formatTime(dist/velocity)
            ], drawPt.add(vectorToScreenCenter.scale(18)), {
                align: "center"
            });
        }
    }
    //#endregion

}